import React, { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Input from "../../components/Input/Input";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import Paginator, { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";
import PageHeader from "../../components/PageHeader/PageHeader";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useToast } from "../../context/ToastContext";
import GroupApi from "../../services/GroupApi";
import UserApi, { ListUsersParams } from "../../services/UserApi";
import { formatPhone } from "../../utilities/phone";
import { Button } from "../../components/Button/Button";
import ListCard from "../../components/ListCard/ListCard";
import { Tag } from "../../components/Tag/Tag";
import IconButton from "../../components/IconButton/IconButton";
import Avatar from "../../components/Avatar/Avatar";
import styles from "./index.module.css";

type GroupOption = {
  label: string;
  value: string;
  color?: string;
};

type UserListItem = {
  id: string;
  name: string;
  username: string;
  email: string;
  phone: string;
  groupName: string;
  active: boolean;
  groupColor?: string;
};

type FilterState = {
  name: string;
  groupIds: string[];
  active: boolean | null;
};

const createEmptyFilters = (): FilterState => ({
  name: "",
  groupIds: [],
  active: null,
});

const DEFAULT_SORT_FIELD = "Name";
const DEFAULT_SORT_DIRECTION: "asc" | "desc" = "asc";
const DEFAULT_GROUP_COLOR = "#6b7280";
const FILTER_STORAGE_KEY = "users:listFilters";

const loadFiltersFromStorage = (): FilterState => {
  if (typeof window === "undefined") {
    return createEmptyFilters();
  }

  try {
    const raw = window.localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) {
      return createEmptyFilters();
    }

    const parsed = JSON.parse(raw);
    const name = typeof parsed?.name === "string" ? parsed.name : "";
    const groupIds = Array.isArray(parsed?.groupIds)
      ? parsed.groupIds.filter((entry: unknown): entry is string => typeof entry === "string")
      : [];
    const active = parsed?.active === true ? true : parsed?.active === false ? false : null;

    return {
      name,
      groupIds,
      active,
    };
  } catch (error) {
    console.warn("Failed to load users filters from storage", error);
    return createEmptyFilters();
  }
};



const normalizeFilters = (filters: FilterState): FilterState => {
  const trimmedName = filters.name.trim();
  return {
    name: trimmedName.length >= 3 ? trimmedName : "",
    groupIds: [...filters.groupIds],
    active: filters.active,
  };
};
const filtersAreEqual = (a: FilterState, b: FilterState): boolean => {
  if (a.name !== b.name || a.active !== b.active || a.groupIds.length !== b.groupIds.length) {
    return false;
  }

  return a.groupIds.every((value, index) => value === b.groupIds[index]);
};

const UsersCrud: React.FC = () => {
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("Messages");
  const { t: translateGeneral } = useTranslation("general");

  const initialFiltersRef = useRef<FilterState | null>(null);
  if (initialFiltersRef.current === null) {
    initialFiltersRef.current = loadFiltersFromStorage();
  }

  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(6);
  const [totalRecords, setTotalRecords] = useState(0);

  const [pendingFilters, setPendingFilters] = useState<FilterState>(() => initialFiltersRef.current!);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(() => normalizeFilters(initialFiltersRef.current!));
  const pendingFiltersRef = useRef<FilterState>(pendingFilters);


  const applyFilters = useCallback(
    (nextFilters: FilterState): void => {
      const normalized = normalizeFilters(nextFilters);
      if (filtersAreEqual(appliedFilters, normalized)) {
        return;
      }

      setPage(0);
      setAppliedFilters(normalized);
    },
    [appliedFilters],
  );

  // const headerItens: IHeader[] = useMemo(
  //   () => [
  //     {
  //       text: translateGeneral("users") || "Users",
  //     },
  //   ],
  //   [translateGeneral],
  // );

  const rowsPerPageOptions = useMemo(() => [6, 12, 24, 36, 48, 60], []);

  useEffect(() => {
    pendingFiltersRef.current = pendingFilters;
  }, [pendingFilters]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(pendingFilters));
    } catch (error) {
      console.warn("Failed to persist users filters", error);
    }
  }, [pendingFilters]);


  const extractErrorMessage = useCallback((error: any): string => {
    if (!error) {
      return "";
    }

    const candidates: any[] = [
      error?.data?.error?.message,
      error?.data?.message,
      Array.isArray(error?.errors)
        ? error.errors.find((msg: any) => typeof msg === "string" && msg.trim().length > 0)
        : null,
      error?.response?.data?.error?.message,
      error?.response?.data?.message,
      error?.message,
    ];

    const match = candidates.find((entry) => typeof entry === "string" && entry.trim().length > 0);
    return match ? match.trim() : "";
  }, []);

  const showErrorToast = useCallback(
    (error: any, fallbackDetail?: string, duration: number = 3000): void => {
      const summary = translateMessages("error") || "Erro";
      const detail = extractErrorMessage(error) || fallbackDetail || summary;
      displayToast("error", summary, detail, false, duration);
    },
    [displayToast, extractErrorMessage, translateMessages],
  );

  useEffect(() => {
    const loadGroups = async (): Promise<void> => {
      try {
        setGroupsLoading(true);
        const response = await GroupApi.ListGroups();
        const rawList: any[] = Array.isArray(response?.data)
          ? (response.data as any[])
          : Array.isArray(response)
            ? (response as any[])
            : [];
        const normalized = rawList
          .map((item) => {
            const id = item?.id ?? item?.groupId ?? item?.groupID ?? item?.Id ?? "";
            if (!id) {
              return null;
            }

            const name = item?.name ?? item?.groupName ?? item?.description ?? item?.GroupName ?? id;
            return {
              label: name,
              description: name,
              value: id,
              color: item?.color ?? item?.groupColor ?? undefined,
            } as GroupOption;
          })
          .filter((entry): entry is GroupOption => Boolean(entry));
        setGroups(normalized);
      } catch (err) {
        showErrorToast(err, translateMessages("groupsLoadFailed") || "Falha ao carregar grupos");
      } finally {
        setGroupsLoading(false);
      }
    };

    loadGroups();
  }, [showErrorToast, translateMessages]);

  const fetchUsers = useCallback(
    async (filters: FilterState, pagination: { pageNumber: number; pageSize: number }): Promise<void> => {
      try {
        setLoading(true);

        const params: ListUsersParams = {
          pageNumber: pagination.pageNumber,
          pageSize: pagination.pageSize,
          name: filters.name.trim() || undefined,
          groupIds: filters.groupIds,
          active: filters.active,
          sortField: DEFAULT_SORT_FIELD,
          sortDirection: DEFAULT_SORT_DIRECTION,
        };

        const response = await UserApi.GetByFilterAsync(params);
        const payload = response?.data ?? response;

        const rawList: any[] = Array.isArray(payload?.Users)
          ? (payload.Users as any[])
          : Array.isArray(payload?.users)
            ? (payload.users as any[])
            : Array.isArray(payload)
              ? (payload as any[])
              : [];

        const normalized: UserListItem[] = rawList
          .map((item) => {
            const id = item?.id ?? item?.userId ?? item?.UserId ?? item?.Id ?? "";
            if (!id) {
              return null;
            }

            const activeValue = typeof item?.active === "boolean"
              ? item.active
              : typeof item?.isActive === "boolean"
                ? item.isActive
                : false;

            return {
              id,
              name: item?.name ?? item?.fullName ?? item?.Name ?? "",
              username: item?.username ?? item?.userName ?? item?.Username ?? "",
              email: item?.email ?? item?.Email ?? "",
              phone: item?.phone ?? item?.phoneNumber ?? item?.Phone ?? "",
              groupName:
                item?.group?.name ??
                item?.groupName ??
                item?.group?.description ??
                item?.GroupName ??
                "",
              groupColor: item?.groupColor ?? item?.GroupColor ?? DEFAULT_GROUP_COLOR,
              active: activeValue,
            } as UserListItem;
          })
          .filter((entry): entry is UserListItem => Boolean(entry));

        const total = typeof payload?.Total === "number"
          ? payload.Total
          : typeof payload?.total === "number"
            ? payload.total
            : normalized.length;

        setUsers(normalized);
        setTotalRecords(total);
      } catch (err) {
        setUsers([]);
        setTotalRecords(0);
        showErrorToast(err, translateMessages("errorSearch") || "Não foi possível carregar os dados", 4000);
      } finally {
        setLoading(false);
      }
    },
    [showErrorToast, translateMessages],
  );

  useEffect(() => {
    fetchUsers(appliedFilters, { pageNumber: page + 1, pageSize: rows });
  }, [appliedFilters, fetchUsers, page, rows]);

  const handleApplyFilters = (): void => {
    applyFilters(pendingFilters);
  };

  const handleClearFilters = (): void => {
    const empty = createEmptyFilters();
    setPendingFilters(empty);
    applyFilters(empty);
  };

  const handlePaginatorChange = (event: PaginatorPageChangeEvent): void => {
    const nextPage = event.rows > 0 ? Math.floor(event.first / event.rows) : 0;
    setPage(nextPage);
    setRows(event.rows);
  };

  const handleDeleteUser = useCallback(
    async (userId: string): Promise<void> => {
      try {
        setDeletingUserId(userId);
        await UserApi.Delete(userId);
        displayToast(
          "success",
          translateForm("Success") || "Success",
          translateForm("userSuccessfullyDeleted") || "User deleted successfully",
          false,
          3000,
        );
        await fetchUsers(appliedFilters, { pageNumber: page + 1, pageSize: rows });
      } catch (error) {
        showErrorToast(error, translateForm("userNotDeleted") || "User not deleted", 4000);
      } finally {
        setDeletingUserId(null);
      }
    },
    [appliedFilters, displayToast, fetchUsers, page, rows, showErrorToast, translateForm],
  );

  const confirmDeleteUser = useCallback(
    (userId: string): void => {
      confirmDialog({
        message: translateForm("userDeleteConfirmMessage") || "Tem certeza de que deseja excluir este usuário?",
        header: translateGeneral("confirmation") || "Confirmação",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: translateForm("Yes") || "Sim",
        rejectLabel: translateForm("No") || "Não",
        acceptClassName: "p-button-accept",
        rejectClassName: "p-button-reject",
        accept: () => void handleDeleteUser(userId),
      });
    },
    [handleDeleteUser, translateForm, translateGeneral],
  );

  const handleNameKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleApplyFilters();
    }
  };

  const activeOptions = useMemo(
    () => [
      { label: translateForm("active") || "Ativo", description: translateForm("active") || "Ativo", value: true },
      { label: translateForm("inactive") || "Inativo", description: translateForm("inactive") || "Inativo", value: false },
    ],
    [translateForm],
  );

  return (
    <div className="overflow-hidden">
      <ConfirmDialog />

      <PageHeader
        title={translateForm("users") || "Usuários"}
        description={translateForm("userDescription") || "Gerencie os usuários do sistema e suas permissões de acesso"}
      >
        <Button
          label={translateForm("register") || "Cadastrar"}
          icon="add"
          severity="primary"
          onClick={() => navigate("/users/create")}
        />
      </PageHeader>

      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

        <FilterPanel
          title={translateForm("filters") || "Filtros"}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        >
          <Input
            type="text"
            label={translateForm("name") || "Nome"}
            placeholder={translateForm("searchByName") || "Pesquisar por nome"}
            value={pendingFilters.name}
            onChange={(val) => setPendingFilters((prev) => ({ ...prev, name: val as string }))}
            onKeyDown={handleNameKeyDown}
            maxLength={120}
          />
          <Input
            type="multiselect"
            label={translateForm("group") || "Grupo"}
            placeholder={translateForm("selectGroups") || "Selecione os grupos"}
            options={groups}
            value={pendingFilters.groupIds}
            onChange={(val) => {
              const nextGroupIds = Array.isArray(val) ? [...(val as string[])] : [];
              const updated = { ...pendingFilters, groupIds: nextGroupIds };
              setPendingFilters(updated);
            }}
            disabled={groupsLoading}
          />
          <Input
            type="select"
            label={translateForm("status") || "Status"}
            placeholder={translateForm("selectStatus") || "Selecione o status"}
            options={activeOptions}
            value={pendingFilters.active}
            onChange={(val) => {
              const upd = { ...pendingFilters, active: val === null ? null : Boolean(val) };
              setPendingFilters(upd);
            }}
          />
        </FilterPanel>

        <section className={styles.resultsPanel}>
          {loading ? (
            <div className={styles.loading}>{translateGeneral("loading") || "Carregando..."}</div>
          ) : users.length === 0 ? (
            <div className={styles.emptyState}>{translateForm("noElementsDefined") || "Nenhum registro encontrado"}</div>
          ) : (
            <div className={styles.cardGrid}>
              {users.map((user) => {
                const statusLabel = user.active
                  ? translateForm("active") || "Ativo"
                  : translateForm("inactive") || "Inativo";
                const formattedPhone = formatPhone(user.phone || "");

                return (
                  <ListCard key={user.id} inactive={!user.active}>
                    <ListCard.Header
                      title={user.name || translateForm("name") || "Nome"}
                      subtitle={user.username}
                      avatar={
                        <Avatar
                          type="person"
                          name={user.name || user.username}
                        />
                      }
                    >
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        <Tag
                          label={user.groupName || translateForm("group") || "Grupo"}
                          severity="neutral"
                        />
                        <Tag
                          label={statusLabel}
                          severity={user.active ? "successInverse" : "errorInverse"}
                        />
                      </div>
                    </ListCard.Header>

                    <ListCard.Content>
                      <ListCard.Metadata layout="stacked">
                        <ListCard.Metadata.Item icon="mail" label={translateForm("email") || "E-mail"} value={user.email || "-"} />
                        <ListCard.Metadata.Item icon="call" label={translateForm("phone") || "Telefone"} value={formattedPhone || "-"} />
                      </ListCard.Metadata>
                    </ListCard.Content>

                    <ListCard.Footer alignment="end" divider>
                      <IconButton
                        icon="edit"
                        variant="text"
                        severity="secondary"
                        aria-label={translateGeneral("edit") || "Editar"}
                        onClick={() => navigate(`/users/${user.id}/edit`)}
                      />
                      <IconButton
                        icon="account_tree"
                        variant="text"
                        severity="primary"
                        aria-label={translateForm("linkPlants", { defaultValue: "Link plants" })}
                        onClick={() => navigate(`/users/${user.id}/link-plant`)}
                      />
                      <IconButton
                        icon="delete"
                        variant="text"
                        severity="danger"
                        aria-label={translateGeneral("delete") || "Excluir"}
                        onClick={() => confirmDeleteUser(user.id)}
                        loading={deletingUserId === user.id}
                        disabled={deletingUserId === user.id}
                      />
                    </ListCard.Footer>
                  </ListCard>
                );
              })}
            </div>
          )}

          <div className={styles.paginatorWrapper}>
            <Paginator
              first={page * rows}
              rows={rows}
              rowsPerPageOptions={rowsPerPageOptions}
              totalRecords={totalRecords}
              onPageChange={handlePaginatorChange}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default UsersCrud;
